import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge, ConfirmDialog, Empty, Field, NativeSelect } from "../common";
import { eur2 as eur, fmtDate, todayISO } from "../../lib/format";
import { cn } from "../../lib/utils";
import { SECTIONS, STATUS_CLS, PAY_METHODS, optionEntries } from "./config";

function FieldInput({ f, value, onChange }) {
  const testid = `fin-field-${f.key}`;
  if (f.type === "select") {
    return <NativeSelect value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>{optionEntries(f.options).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</NativeSelect>;
  }
  if (f.type === "textarea") return <Textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
  return <Input type={f.type || "text"} step={f.type === "number" ? "0.01" : undefined} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
}

const toPayload = (fields, v) => Object.fromEntries(fields.map((f) => {
  const raw = v[f.key];
  if (f.type === "number") return [f.key, Number(raw)];
  return [f.key, raw === "" || raw === undefined ? null : raw];
}));

const missingRequired = (fields, v) => fields.find((f) => f.required && (v[f.key] === undefined || v[f.key] === ""));

function saveEntry(entity, siteId, item, body) {
  if (item) return api.put(`/finance/entries/${entity}/${item.id}`, body);
  return api.post(`/finance/entries/sites/${siteId}/${entity}`, body);
}

function useCostCategories(enabled) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    api.get("/cost-categories").then((r) => setItems(r.data.items.map((x) => x.name)));
  }, [enabled]);
  return items;
}

const withCategories = (fields, categories, current) => {
  if (!categories) return fields;
  const options = current && !categories.includes(current) ? [...categories, current] : categories;
  return fields.map((f) => (f.key === "category" ? { ...f, options } : f));
};

function useEntryForm(entity, siteId, item, onDone) {
  const cfg = SECTIONS[entity];
  const [v, setV] = useState(item || cfg.defaults());
  const [busy, setBusy] = useState(false);
  const categories = useCostCategories(entity === "purchases");
  useEffect(() => {
    if (categories?.length && !item) {
      setV((p) => (categories.includes(p.category) ? p : { ...p, category: categories[0] }));
    }
  }, [categories, item]);
  const fields = useMemo(() => withCategories(cfg.fields, categories, item?.category), [cfg.fields, categories, item]);
  const save = async () => {
    const missing = missingRequired(cfg.fields, v);
    if (missing) return toast.error(`Compila il campo "${missing.label}"`);
    setBusy(true);
    try {
      await saveEntry(entity, siteId, item, toPayload(cfg.fields, v));
      toast.success("✓ Salvato");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return { cfg, v, setV, busy, fields, save };
}

function EntryForm({ entity, siteId, item, onClose, onSaved }) {
  const { cfg, v, setV, busy, fields, save } = useEntryForm(entity, siteId, item, () => { onSaved(); onClose(); });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-popover max-w-2xl" data-testid={`fin-form-${entity}`}>
        <DialogHeader><DialogTitle>{item ? "Modifica" : cfg.add}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {fields.map((f) => (
            <Field key={f.key} label={f.label} className={f.wide ? "sm:col-span-2" : ""}>
              <FieldInput f={f} value={v[f.key]} onChange={(val) => setV((p) => ({ ...p, [f.key]: val }))} />
            </Field>
          ))}
        </div>
        <Button onClick={save} disabled={busy} data-testid="fin-form-save-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva</Button>
      </DialogContent>
    </Dialog>
  );
}

function PaymentForm({ entity, item, onSaved }) {
  const [p, setP] = useState({ pay_date: todayISO(), amount: item.residual, method: PAY_METHODS[0], notes: "" });
  const add = async () => {
    try {
      await api.post(`/finance/entries/${entity}/${item.id}/payments`, { ...p, amount: Number(p.amount) });
      toast.success("✓ Registrato");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const set = (k) => (e) => setP((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div className="grid sm:grid-cols-[140px_130px_140px_1fr_auto] gap-2 items-end border-t border-border pt-3">
      <Field label="Data"><Input type="date" value={p.pay_date} onChange={set("pay_date")} data-testid="fin-pay-date" /></Field>
      <Field label="Importo €"><Input type="number" step="0.01" value={p.amount} onChange={set("amount")} data-testid="fin-pay-amount" /></Field>
      <Field label="Modalità"><NativeSelect value={p.method} onChange={set("method")} data-testid="fin-pay-method">{PAY_METHODS.map((m) => <option key={m}>{m}</option>)}</NativeSelect></Field>
      <Field label="Note"><Input value={p.notes} onChange={set("notes")} data-testid="fin-pay-notes" /></Field>
      <Button onClick={add} disabled={item.residual <= 0} data-testid="fin-pay-add-btn"><Plus className="h-4 w-4" /></Button>
    </div>
  );
}

function PaymentsDialog({ entity, item, onClose, onSaved }) {
  const cfg = SECTIONS[entity];
  const remove = async (pid) => {
    try {
      await api.delete(`/finance/entries/${entity}/payments/${pid}`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-popover max-w-3xl" data-testid="fin-payments-dialog">
        <DialogHeader><DialogTitle>{cfg.paymentsTitle}</DialogTitle></DialogHeader>
        <div className="text-sm">{cfg.row(item).title}</div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div className="surface p-2"><div className="eyebrow">Importo</div><div className="num">{eur(item.amount)}</div></div>
          <div className="surface p-2"><div className="eyebrow">{cfg.paidLabel}</div><div className="num text-emerald-300">{eur(item.paid)}</div></div>
          <div className="surface p-2"><div className="eyebrow">{cfg.residualLabel}</div><div className="num text-amber-300" data-testid="fin-pay-residual">{eur(item.residual)}</div></div>
        </div>
        <ul className="divide-y divide-border/60 text-sm">
          {item.payments.map((p) => (
            <li key={p.id} className="flex items-center gap-3 py-2" data-testid={`fin-payment-${p.id}`}>
              <span className="num w-24">{fmtDate(p.pay_date)}</span><span className="flex-1">{p.method} {p.notes && `· ${p.notes}`}</span>
              <span className="num">{eur(p.amount)}</span>
              <Button size="icon" variant="ghost" className="text-red-400 h-8 w-8" onClick={() => remove(p.id)} data-testid={`fin-payment-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
          {!item.payments.length && <li className="py-2 text-muted-foreground">Nessun movimento registrato</li>}
        </ul>
        <PaymentForm key={item.residual} entity={entity} item={item} onSaved={onSaved} />
      </DialogContent>
    </Dialog>
  );
}

function EntryAmount({ cfg, r }) {
  const open = r.residual > 0;
  return (
    <div className="text-right num">
      <div className="font-semibold">{eur(r.amount)}</div>
      {cfg.paymentsTitle && <div className={cn("text-xs", open ? "text-amber-300" : "text-emerald-300")}>{open ? `${cfg.residualLabel}: ${eur(r.residual)}` : "Saldato"}</div>}
    </div>
  );
}

function EntryActions({ entity, cfg, r, onEdit, onDelete, onPayments }) {
  return (
    <div className="flex gap-1">
      {cfg.paymentsTitle && <Button size="sm" variant="outline" onClick={() => onPayments(r)} data-testid={`fin-payments-btn-${r.id}`}><Wallet className="h-4 w-4 mr-1" />{cfg.paidLabel === "Pagato" ? "Pagamenti" : "Incassi"}</Button>}
      <Button size="icon" variant="ghost" onClick={() => onEdit(r)} data-testid={`fin-edit-${entity}-${r.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" className="text-red-400" onClick={() => onDelete(r)} data-testid={`fin-delete-${entity}-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}

function EntryRow({ entity, r, onEdit, onDelete, onPayments }) {
  const cfg = SECTIONS[entity];
  const view = cfg.row(r);
  const overdue = Boolean(view.due) && r.residual > 0 && view.due < todayISO();
  return (
    <li className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-3" data-testid={`fin-row-${entity}-${r.id}`}>
      <span className="num text-xs text-muted-foreground w-24">{fmtDate(view.date)}</span>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{view.title}</div>
        <div className="text-xs text-muted-foreground">{view.subtitle}{view.due && <span className={cn(overdue && "text-red-400 font-semibold")}> · scad. {fmtDate(view.due)}</span>}</div>
      </div>
      {view.badge && <Badge className={STATUS_CLS[view.badge]}>{view.badgeLabel}</Badge>}
      <EntryAmount cfg={cfg} r={r} />
      <EntryActions entity={entity} cfg={cfg} r={r} onEdit={onEdit} onDelete={onDelete} onPayments={onPayments} />
    </li>
  );
}

export default function EntryTab({ entity, siteId, items, reload }) {
  const cfg = SECTIONS[entity];
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const [payId, setPayId] = useState(null);
  const payItem = items.find((x) => x.id === payId);
  const remove = async () => {
    try {
      await api.delete(`/finance/entries/${entity}/${del.id}`);
      toast.success("✓ Eliminato");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDel(null);
  };
  return (
    <section className="surface p-5" data-testid={`fin-tab-${entity}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="font-semibold">{cfg.title}</h3>
        <Button onClick={() => setForm({ item: null })} data-testid={`fin-add-${entity}-btn`}><Plus className="h-4 w-4 mr-2" />{cfg.add}</Button>
      </div>
      {!items.length && <Empty title={cfg.empty} />}
      <ul className="divide-y divide-border/60">
        {items.map((r) => <EntryRow key={r.id} entity={entity} r={r} onEdit={(x) => setForm({ item: x })} onDelete={setDel} onPayments={(x) => setPayId(x.id)} />)}
      </ul>
      {form && <EntryForm entity={entity} siteId={siteId} item={form.item} onClose={() => setForm(null)} onSaved={reload} />}
      {payItem && <PaymentsDialog entity={entity} item={payItem} onClose={() => setPayId(null)} onSaved={reload} />}
      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} title="Eliminare questa voce?" description={del ? cfg.row(del).title : undefined} onConfirm={remove} />
    </section>
  );
}
